import {
  CreateParams,
  DataProvider,
  DeleteOneParams,
  UpdateParams,
} from "@refinedev/core";
import { IDataContextProvider } from "@refinedev/core/dist/contexts/data/IDataContext";
import { AxiosInstance } from "axios";
import dayjs from "dayjs";

import axAPI from "../../../api/axios-instance";
import { dateFormat } from "../../../helpers";

const AWARD_URL = "cms/awards/reward";
const AWARDS_LIST_URL = "cms/awards/reward/list";
const API_DATE_FORMAT = "YYYY-MM-DD[T]00:00:00";

type AwardFormValues = {
  text?: string;
  dateFrom?: string | null;
  awardTypeId?: number | string;
};

type AwardMeta = {
  id?: number | string;
};

export const awardsDataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axAPI
): DataProvider =>
  <IDataContextProvider>{
    getApiUrl: () => apiUrl,
    getList: async () => {
      try {
        const { data } = await httpClient.get(AWARDS_LIST_URL);

        return {
          data,
          total: data.length,
        };
      } catch (error: unknown) {
        return Promise.reject(error);
      }
    },
    getOne: async ({ id }) => {
      try {
        const { data } = await httpClient.get(AWARD_URL, {
          params: { id },
        });

        return {
          data: {
            ...data,
            dateFrom: data?.dateFrom ? dayjs(data.dateFrom) : undefined,
          },
        };
      } catch (error: unknown) {
        return Promise.reject(error);
      }
    },
    create: async ({ variables }: CreateParams<AwardFormValues>) => {
      try {
        const { data } = await httpClient.post(AWARD_URL, {
          text: variables.text,
          dateFrom: variables.dateFrom
            ? dateFormat(variables.dateFrom, API_DATE_FORMAT)
            : null,
          awardTypeId: variables.awardTypeId
            ? Number(variables.awardTypeId)
            : null,
        });

        return {
          data: {
            ...variables,
            result: data,
          },
        };
      } catch (error: unknown) {
        return Promise.reject(error);
      }
    },
    update: async ({ id, variables }: UpdateParams<AwardFormValues>) => {
      try {
        const { data } = await httpClient.put(AWARD_URL, {
          id: Number(id),
          text: variables.text,
          dateFrom: variables.dateFrom
            ? dateFormat(variables.dateFrom, API_DATE_FORMAT)
            : null,
          awardTypeId: variables.awardTypeId
            ? Number(variables.awardTypeId)
            : null,
        });

        return {
          data: {
            id,
            ...variables,
            result: data,
          },
        };
      } catch (error: unknown) {
        return Promise.reject(error);
      }
    },
    deleteOne: async ({ id, meta }: DeleteOneParams) => {
      const awardId = (meta as AwardMeta | undefined)?.id ?? id;

      try {
        const { data } = await httpClient.delete(AWARD_URL, {
          params: { id: Number(awardId) },
        });

        return {
          data: {
            id: awardId,
            result: data,
          },
        };
      } catch (error: unknown) {
        return Promise.reject(error);
      }
    },
  };
